import { useCallback, useEffect, useState } from 'react'

const PERMISSION_NAMES: PermissionName[] = [
  'microphone' as PermissionName,
  'camera' as PermissionName,
]

/*
Definitive check for permission states. Currently chrome is the only browser that supports querying the permission state
of microphone and camera. Until all browsers are supported there is additional code inside use_device_manager.ts to infer
the permission state, which can be deleted once all browsers support.
https://developer.mozilla.org/en-US/docs/Web/API/Permissions/query
*/

export const useDevicePermissionStates = () => {
  const [permissionStates, setPermissionStates] = useState<PermissionStates>({})
  const [listeners, setListeners] = useState<any>({})

  const makeOnChange = useCallback(
    (name) => (event: any) => {
      setPermissionStates((prevState) => {
        return {
          ...prevState,
          [name]: event.target.state,
        }
      })
    },
    [],
  )

  const refresh = useCallback(async () => {
    const newState: PermissionStates = {}
    for (let i = 0; i < PERMISSION_NAMES.length; i++) {
      const name = PERMISSION_NAMES[i]
      try {
        const permissionInfo = await navigator.permissions.query({ name })
        if (permissionInfo) {
          newState[name] = permissionInfo.state
          const onChange = makeOnChange(name)
          setListeners((prevState: any) => {
            return {
              ...prevState,
              [name]: { el: permissionInfo, func: onChange },
            }
          })
          permissionInfo.addEventListener('change', onChange)
        }
      } catch {
        // browser does not support, do nothing
      }
    }
    setPermissionStates(newState)
  }, [makeOnChange])

  useEffect(() => {
    refresh()

    return () => {
      PERMISSION_NAMES.forEach((name) => {
        if (listeners[name]) {
          const { el, func } = listeners[name]
          el.removeEventListener('change', func)
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { permissionStates }
}

export type PermissionStates = { [key: string]: PermissionStatus['state'] }
